<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import type { MaterialTabType } from '@/components/materials/type/type'
import GeneralMaterials from '@/components/materials/GeneralMaterials.vue'
import RubbleMound from '@/components/materials/RubbleMound.vue'
import Gabion from '@/components/materials/Gabion.vue'
import ConcreteElements from '@/components/materials/ConcreteElements.vue'
import FilterNet from '@/components/materials/FilterNet.vue'
import CustomRockGrading from '@/components/materials/custom-rock-grading/CustomRockGrading.vue'
import { useMaterialStore } from '@/stores/material'
import { storeToRefs } from 'pinia'

const materialStore = useMaterialStore()

const { currentItem } = storeToRefs(materialStore)

const tab = ref<MaterialTabType>('GENERAL')

const tabs = ref([
  { value: 'GENERAL', label: 'General materials', isShown: true },
  { value: 'RUBBLE_MOUND', label: 'Rubble mound', isShown: true },
  { value: 'GABION', label: 'Gabion', isShown: true },
  { value: 'CONCRETE_ELEMENT', label: 'Concrete elements', isShown: true },
  { value: 'FILTER_NET', label: 'FilterNet', isShown: true },
  { value: 'CUSTOM_ROCK_GRADINGS', label: 'Custom rock gradings', isShown: true }
])

const activeTab = (tabValue: string) => {
  return tab.value === tabValue ? 'active-tab' : ''
}

const updateTabVisibility = () => {
  if (!currentItem.value?.material_type) return
  tabs.value.forEach((tab) => {
    if (['GENERAL', 'CUSTOM_ROCK_GRADINGS'].includes(tab.value)) return
    tab.isShown = currentItem.value?.material_type.includes(tab.value)
  })
}

nextTick(updateTabVisibility)

watch(() => currentItem.value?.material_type, updateTabVisibility, { deep: true })
</script>

<template>
  <template v-if="!currentItem">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <v-tabs
      v-model="tab"
      bg-color="transparent"
      color="primary"
      class="sticky-tabs"
      style="z-index: 1"
    >
      <template v-for="item in tabs" :key="item.value">
        <v-tab
          v-if="item.isShown"
          :value="item.value"
          :class="['text-none', activeTab(item.value)]"
          rounded="t-lg"
        >
          {{ item.label }}
        </v-tab>
      </template>
    </v-tabs>

    <v-window v-model="tab" class="px-8 py-6">
      <v-window-item value="GENERAL">
        <GeneralMaterials v-if="currentItem" />
      </v-window-item>
      <v-window-item value="RUBBLE_MOUND">
        <RubbleMound v-if="currentItem" />
      </v-window-item>
      <v-window-item value="GABION">
        <Gabion v-if="currentItem" />
      </v-window-item>
      <v-window-item value="CONCRETE_ELEMENT">
        <ConcreteElements v-if="currentItem" />
      </v-window-item>
      <v-window-item value="FILTER_NET">
        <FilterNet v-if="currentItem" />
      </v-window-item>
      <v-window-item value="CUSTOM_ROCK_GRADINGS">
        <CustomRockGrading />
      </v-window-item>
    </v-window>
  </template>
</template>

<style scoped>
.sticky-tabs {
  position: sticky;
  top: 0;
  z-index: 1;
}

.v-tab {
  color: #009eab;
}

.v-tab :deep(.v-tab__slider) {
  color: #009eab;
}

.active-tab {
  background: #ccecee;
  color: #303030 !important;
}
</style>
